
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { fetchPracticeSummary } from '../../store/slices/practiceSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserMd,
  faUsers,
  faUserNurse,
  faUserInjured,
  faCalendarAlt,
  faFileImport,
  faUserCheck,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';

// Import components from PracticeManagement
import PracticeDetails from '../PracticeManagement/PracticeDetails/PracticeDetails';
import AdminManagement from '../PracticeManagement/AdminManagement/AdminManagement';
import ProviderManagement from '../PracticeManagement/ProviderManagement/ProviderManagement';
import CaregiverManagement from '../PracticeManagement/CaregiverManagement/CaregiverManagement';
import PatientManagement from '../PracticeManagement/PatientManagement/PatientManagement';
import SystemCaregiverManagement from '../PracticeManagement/SystemCaregiverManagement/SystemCaregiverManagement';
import ScheduleTargetSettings from '../PracticeManagement/ScheduleTargetSettings/ScheduleTargetSettings';
import ImportPatients from '../PracticeManagement/ImportPatients/ImportPatients';
import PracticeInlineEventLogs from '../PracticeManagement/PracticeDashboard/PracticeInlineEventLogs';
import {
  getPracticeTabEventLogModule,
  PRACTICE_EVENT_LOGS_QUERY,
} from '../PracticeManagement/PracticeDashboard/practiceEventLogs';

import '../PracticeManagement/PracticeDashboard/PracticeDashboard.css';
import { usePracticeDashboardTabIndicator } from '../PracticeManagement/PracticeDashboard/usePracticeDashboardTabIndicator';
import { useMountedDashboardTabs } from '../PracticeManagement/PracticeDashboard/useMountedDashboardTabs';
import PracticeDashboardTabPane from '../PracticeManagement/PracticeDashboard/PracticeDashboardTabPane';
import { buildProgramRibbonItems } from '../../utils/careProgramUtils';
import { formatDateOnlyDash } from '../../utils/dateOnlyUtils';
import { getPracticeMonitoringSectionFlags } from '../../utils/practiceMonitoringUtils';

/**
 * Local ErrorBoundary to avoid adding external deps.
 * Shows a visible fallback instead of leaving a blank area when a child throws.
 */
class LocalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // keep logging so devs can see stack traces in console
    console.error('[LocalErrorBoundary] caught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
    // optionally call parent's onReset if provided
    if (typeof this.props.onReset === 'function') {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.error) {
      const Fallback = this.props.FallbackComponent;
      // render user-provided fallback or minimal fallback
      if (Fallback) {
        return <Fallback error={this.state.error} resetErrorBoundary={this.reset} />;
      }
      return (
        <div style={{ padding: 12, border: '1px solid #e74c3c', background: '#fff5f5', borderRadius: 6 }}>
          <strong>Something went wrong.</strong>
          <div style={{ marginTop: 8, fontSize: 13 }}>{this.state.error?.message || 'Unknown error'}</div>
          <div style={{ marginTop: 8 }}>
            <button className="btn btn-sm btn-outline-secondary" onClick={this.reset}>Retry</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* Visible fallback UI for schedule tab errors */
const ScheduleErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div style={{
      border: '1px solid #e74c3c',
      background: '#fff5f5',
      color: '#a94442',
      padding: 12,
      borderRadius: 6,
      margin: 8
    }}>
      <strong>Schedule UI failed to render.</strong>
      <div style={{ marginTop: 6, fontSize: 13 }}>
        {error?.message || 'Unknown render error in ScheduleTargetSettings'}
      </div>
      <div style={{ marginTop: 8 }}>
        <button className="btn btn-sm btn-outline-secondary" onClick={resetErrorBoundary}>
          Retry
        </button>
      </div>
    </div>
  );
};

const ProviderDashboard = () => {
  const { user } = useSelector(state => state.auth);
  const practiceId = user?.practice_id;
  const { currentPractice } = useSelector(state => state.practice ?? {});

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  // Practice data from Redux store
  const [practiceData, setPracticeData] = useState(() => {
    if (!currentPractice) return null;
    return String(currentPractice.id) === String(practiceId) ? currentPractice : null;
  });
  const [loading, setLoading] = useState(() => {
    if (!currentPractice) return true;
    return String(currentPractice.id) !== String(practiceId);
  });
  const tabContentRef = useRef(null);

  // All tabs (including previously in More dropdown)
  const tabs = useMemo(() => ([
    { id: 'dashboard', label: 'Practice', icon: faChartLine },
    { id: 'admin', label: 'Admins', icon: faUsers },
    { id: 'provider', label: 'Providers', icon: faUserMd },
    { id: 'caregiver', label: 'Caregivers', icon: faUserNurse },
    { id: 'patient', label: 'Patients', icon: faUserInjured },
    { id: 'system-caregiver', label: 'System Caregivers', icon: faUserCheck },
    { id: 'schedule', label: 'Schedule & Targets', icon: faCalendarAlt },
    { id: 'import', label: 'Import Patients', icon: faFileImport }
  ]), []);

  const getTabFromSearch = useCallback((search) => {
    const params = new URLSearchParams(search);
    const requested = params.get('tab');
    return tabs.some(tab => tab.id === requested) ? requested : 'dashboard';
  }, [tabs]);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));
  const { tabsWrapRef, tabIndicatorStyle, indicatorReady } = usePracticeDashboardTabIndicator(
    activeTab,
    tabs.length
  );
  const mountedTabs = useMountedDashboardTabs(activeTab, practiceId);
  
  const selectedProgramId = useMemo(() => {
    const programParam = String(searchParams.get('program') || '').trim().toLowerCase();
    return activeTab === 'patient' && programParam ? programParam : '';
  }, [searchParams, activeTab]);

  const scheduleLogSection = useMemo(() => {
    if (activeTab !== 'schedule') return null;
    const section = String(searchParams.get('scheduleLogSection') || '').trim();
    return section || null;
  }, [searchParams, activeTab]);

  const openScheduleEventLog = useCallback(
    (section) => {
      const params = new URLSearchParams(location.search);
      params.set('tab', 'schedule');
      // 'all' opens full Schedule event logs; a specific section filters the list.
      params.set('scheduleLogSection', section || 'all');
      params.delete(PRACTICE_EVENT_LOGS_QUERY);
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true, state: location.state }
      );
    },
    [location.pathname, location.search, location.state, navigate]
  );

  const eventLogsActive = searchParams.get(PRACTICE_EVENT_LOGS_QUERY) === '1';

  const openTabEventLogs = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', activeTab);
    params.set(PRACTICE_EVENT_LOGS_QUERY, '1');
    params.delete('viewId');
    params.delete('scheduleLogSection');
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true, state: location.state }
    );
  }, [activeTab, location.pathname, location.search, location.state, navigate]);

  const renderWithInlineEventLogs = useCallback((tabId, content) => {
    const moduleId = getPracticeTabEventLogModule(tabId);
    if (eventLogsActive && activeTab === tabId && moduleId) {
      return (
        <PracticeInlineEventLogs
          moduleId={moduleId}
          practiceId={practiceId}
        />
      );
    }
    return content;
  }, [eventLogsActive, activeTab, practiceId]);

  const normalizeProgramKey = (name) => {
    if (!name) return '';
    return String(name).trim().toLowerCase().replace(/\s+/g, '-');
  };

  const selectedProgramRange = useMemo(() => {
    if (!selectedProgramId || !Array.isArray(practiceData?.enrolled_programs)) return null;
    return practiceData.enrolled_programs
      .map((program) => {
        if (!program) return null;
        const programName = typeof program === 'string'
          ? program
          : String(program.short_name || program.id || program.full_name || program.program || program.label || '').trim();
        if (!programName) return null;
        return {
          key: normalizeProgramKey(programName),
          name: programName,
          start_date: typeof program === 'string'
            ? null
            : program.start_date || program.rpm_start_date || program.startDate || null,
          end_date: typeof program === 'string'
            ? null
            : program.end_date || program.rpm_end_date || program.endDate || null
        };
      })
      .filter(Boolean)
      .find((range) => range.key === selectedProgramId) || null;
  }, [selectedProgramId, practiceData?.enrolled_programs]);

  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlTab = params.get('tab');

    if (urlTab && tabs.some((tab) => tab.id === urlTab)) {
      setActiveTab((prev) => (prev === urlTab ? prev : urlTab));
      return;
    }

    if (!urlTab) {
      params.set('tab', 'dashboard');
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true }
      );
    }
  }, [location.search, location.pathname, navigate, tabs]);

  useEffect(() => {
    const loadPracticeData = async () => {
      if (!practiceId) {
        setLoading(false);
        return;
      }
      try {
        // Reuse cached practice data if it matches this practiceId.
        if (currentPractice && String(currentPractice.id) === String(practiceId)) {
          setPracticeData(currentPractice);
          setLoading(false);
          return;
        }

        setLoading(true);
        const result = await dispatch(fetchPracticeSummary(practiceId));
        if (fetchPracticeSummary.fulfilled.match(result)) {
          setPracticeData(result.payload.data);
        } else {
          console.error('Failed to load practice:', result.payload);
        }
      } catch (error) {
        console.error('Error loading practice:', error);
      } finally {
        setLoading(false);
      }
    };

    if (practiceId) {
      loadPracticeData();
    }
  }, [practiceId, dispatch, currentPractice]);

  useEffect(() => {
    if (!practiceId) return undefined;
    const refreshCounts = async (event) => {
      const changedId = event?.detail?.practiceId;
      if (changedId != null && String(changedId) !== String(practiceId)) return;
      const sum = await dispatch(fetchPracticeSummary(practiceId));
      if (fetchPracticeSummary.fulfilled.match(sum)) {
        setPracticeData(sum.payload.data);
      }
    };
    window.addEventListener('evitals:practice-patients-changed', refreshCounts);
    return () => window.removeEventListener('evitals:practice-patients-changed', refreshCounts);
  }, [practiceId, dispatch]);

  // Update breadcrumbs when practice data loads and tab changes
  useEffect(() => {
    if (practiceData) {
      const currentTab = tabs.find(tab => tab.id === activeTab);
      
      if (activeTab === 'dashboard') {
        updateBreadcrumbs([]);
      } else if (eventLogsActive) {
        const tabPath = `${location.pathname}?tab=${activeTab}`;
        updateBreadcrumbs([
          { label: practiceData.practice_name, path: '/dashboard?tab=dashboard', active: false },
          { label: currentTab?.label || 'Practice', path: tabPath, active: false },
          { label: 'Event Logs', path: null, active: true }
        ]);
      } else if (activeTab === 'schedule' && scheduleLogSection) {
        updateBreadcrumbs([
          { label: practiceData.practice_name, path: '/dashboard?tab=dashboard', active: false },
          { label: currentTab?.label || 'Schedule & Targets', path: `${location.pathname}?tab=schedule`, active: false },
          { label: 'Event Logs', path: null, active: true }
        ]);
      } else {
        const baseCrumbs = [
          { label: practiceData.practice_name, path: '/dashboard?tab=dashboard', active: false },
          { label: currentTab?.label || 'Practice', path: null, active: true }
        ];
        updateBreadcrumbs(baseCrumbs);
      }
    }
  }, [practiceData, activeTab, tabs, updateBreadcrumbs, eventLogsActive, scheduleLogSection, location.pathname]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(location.search);
    params.set('tab', tabId);
    params.delete('viewId');
    if (tabId !== 'patient') {
      params.delete('program');
    }
    // Always clear so clicking Schedule & Targets returns to settings, not event logs
    params.delete('scheduleLogSection');
    params.delete(PRACTICE_EVENT_LOGS_QUERY);
    navigate({
      pathname: location.pathname,
      search: params.toString()
    }, { replace: true });
  };

  const renderTabContent = () => {
    if (!practiceId) return null;

    return (
      <div className="practice-dashboard-tab-panel">
        <PracticeDashboardTabPane tabId="dashboard" activeTab={activeTab} mountedTabs={mountedTabs}>
          {renderWithInlineEventLogs(
            'dashboard',
            <PracticeDetails
              practiceData={practiceData}
              embeddedInPracticeDashboard
              practiceId={practiceId}
              onOpenEventLogs={openTabEventLogs}
            />
          )}
        </PracticeDashboardTabPane>
        <PracticeDashboardTabPane tabId="admin" activeTab={activeTab} mountedTabs={mountedTabs}>
          {renderWithInlineEventLogs(
            'admin',
            <AdminManagement practiceId={practiceId} onOpenEventLogs={openTabEventLogs} />
          )}
        </PracticeDashboardTabPane>
        <PracticeDashboardTabPane tabId="provider" activeTab={activeTab} mountedTabs={mountedTabs}>
          {renderWithInlineEventLogs(
            'provider',
            <ProviderManagement practiceId={practiceId} onOpenEventLogs={openTabEventLogs} />
          )}
        </PracticeDashboardTabPane>
        <PracticeDashboardTabPane tabId="caregiver" activeTab={activeTab} mountedTabs={mountedTabs}>
          {renderWithInlineEventLogs(
            'caregiver',
            <CaregiverManagement practiceId={practiceId} onOpenEventLogs={openTabEventLogs} />
          )}
        </PracticeDashboardTabPane>
        <PracticeDashboardTabPane tabId="patient" activeTab={activeTab} mountedTabs={mountedTabs}>
          {renderWithInlineEventLogs(
            'patient',
            <PatientManagement
              practiceId={practiceId}
              embeddedInProviderDashboard
              dashboardTabKey="patient"
              isDashboardTabActive={activeTab === 'patient'}
              onOpenEventLogs={openTabEventLogs}
            />
          )}
        </PracticeDashboardTabPane>
        <PracticeDashboardTabPane tabId="system-caregiver" activeTab={activeTab} mountedTabs={mountedTabs}>
          {renderWithInlineEventLogs(
            'system-caregiver',
            <SystemCaregiverManagement practiceId={practiceId} onOpenEventLogs={openTabEventLogs} />
          )}
        </PracticeDashboardTabPane>
        <PracticeDashboardTabPane tabId="schedule" activeTab={activeTab} mountedTabs={mountedTabs}>
          <div style={{ display: 'block' }}>
            <LocalErrorBoundary FallbackComponent={ScheduleErrorFallback}>
              {scheduleLogSection ? (
                <PracticeInlineEventLogs
                  moduleId="Schedule"
                  practiceId={practiceId}
                  sectionFilter={scheduleLogSection === 'all' ? null : scheduleLogSection}
                />
              ) : (
                <ScheduleTargetSettings
                  practiceId={practiceId}
                  onOpenEventLog={openScheduleEventLog}
                  isParentTabActive={activeTab === 'schedule' && !scheduleLogSection}
                  enabledMonitoring={getPracticeMonitoringSectionFlags(practiceData?.selected_monitoring_types)}
                />
              )}
            </LocalErrorBoundary>
          </div>
        </PracticeDashboardTabPane>
        <PracticeDashboardTabPane tabId="import" activeTab={activeTab} mountedTabs={mountedTabs}>
          <ImportPatients practiceId={practiceId} />
        </PracticeDashboardTabPane>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="provider-dashboard-loading-container app-page-loader">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!practiceId) {
    return (
      <div className="alert alert-warning" role="alert">
        No practice associated with this provider account.
      </div>
    );
  }

  if (!practiceData) {
    // If loaded but no data
    return (
      <div className="alert alert-danger" role="alert">
        Practice not found!
      </div>
    );
  }

  const formatBillingPeriodDate = (iso) => {
    if (!iso) return 'N/A';
    return formatDateOnlyDash(iso) || 'N/A';
  };

  const renderProgramSummary = () => {
    const enrolled = Array.isArray(practiceData.enrolled_programs) ? practiceData.enrolled_programs : [];
    if (enrolled.length === 0) {
      return (
        <div className="practice-dashboard-program-ribbon">
          <span className="program-ribbon-text text-muted">No care programs enrolled</span>
        </div>
      );
    }

    const programs = buildProgramRibbonItems(enrolled, practiceData.program_counts || {});

    return (
      <div className="practice-dashboard-program-ribbon">
        {programs.map((prog) => (
          <div key={prog.id} className="program-ribbon-item">
            <FontAwesomeIcon icon={prog.icon} className="program-ribbon-icon" style={{ color: prog.color }} />
            <span className="program-ribbon-text">
              <span className="program-ribbon-count">{prog.count}</span>{' '}
              {prog.displayName || prog.full_name || prog.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderProgramDates = () => {
    if (!selectedProgramId || !selectedProgramRange) {
      return null;
    }

    return (
      <div className="practice-dashboard-billing-periods">
        <div key={selectedProgramRange.key} className="practice-dashboard-period-row">
          <span className="practice-dashboard-period-name">{selectedProgramRange.name}</span>
          <span className="practice-dashboard-period-value">
            {formatBillingPeriodDate(selectedProgramRange.start_date)} - {formatBillingPeriodDate(selectedProgramRange.end_date)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="practice-dashboard provider-dashboard-ermp">
      <div className="practice-dashboard-single-card">
        {/* Practice Header */}
        <section className="practice-dashboard-card-header">
          <div className="practice-dashboard-header-main">
            <div className="practice-dashboard-header-left">
              <h2 className="practice-dashboard-title">{practiceData.practice_name}</h2>
              {renderProgramSummary()}
            </div>
            {activeTab === 'patient' && selectedProgramRange && (
              <div className="practice-dashboard-header-right">
                <div className="practice-dashboard-billing-info">
                  <span className="billing-label">Billing Dates</span>
                  {renderProgramDates()}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Tab Navigation */}
        <nav className="practice-dashboard-nav" aria-label="Practice sections">
          <div className="practice-dashboard-tabs-wrap" ref={tabsWrapRef}>
            <span
              className={`practice-dashboard-tab-indicator${indicatorReady ? ' practice-dashboard-tab-indicator--ready' : ''}`}
              style={tabIndicatorStyle}
              aria-hidden="true"
            />
            <ul className="practice-dashboard-tabs">
              {tabs.map((tab) => (
                <li key={tab.id} className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}>
                  <button
                    onClick={() => handleTabChange(tab.id)}
                    className="tab-button"
                    type="button"
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Tab Content */}
        <section className="practice-dashboard-card-body" ref={tabContentRef}>
          {renderTabContent()}
        </section>
      </div>
    </div>
  );
};

export default ProviderDashboard;


